/**
 * Client-side document conversion for directive uploads.
 *
 * Images (JPEG/PNG) are re-encoded to a single lossy WebP at quality 0.85 so the
 * server stores one uniform image format and keyon needs no image libraries.
 * PDFs pass through untouched. The original file is discarded — only the File
 * returned here is uploaded.
 *
 * convertUploadFile(file) resolves to a ready-to-POST File, or throws so the
 * caller can show a re-pick message and never POST something the server would
 * reject. Every image takes exactly one lossy re-encode — the PNG intermediate
 * on the HEIC fallback path is lossless, so HEIC is not double-encoded.
 */

export const WEBP_QUALITY = 0.85;

// Authoritative upload cap lives on the server (keyon php.ini = 20M). This is a
// looser *pre-conversion* guard on the raw pick: a large HEIC can compress well
// under 20M after WebP encoding, so we only fail-fast on absurd inputs to avoid
// hanging/OOM-ing the tab before we ever produce something to size-check.
export const MAX_INPUT_BYTES = 40 * 1024 * 1024;

// Single friendly message for every non-recoverable pick: oversized input,
// decode failure, OOM, or a failed WASM-decoder load. The user's next action
// (re-pick a different file) is the same regardless of cause.
export const REPICK_MESSAGE =
  "We couldn't process that file. Please upload a JPG, PNG, HEIC, or PDF (max 20 MB).";

const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png"];

/** Pre-conversion guard: true when the raw pick is small enough to attempt. */
export function isWithinInputLimit(file) {
  return !!file && file.size <= MAX_INPUT_BYTES;
}

/**
 * Convert a picked file into the artifact to upload.
 * @param {File} file
 * @param {{maxEdge?: number}} [options] maxEdge scales the image down so its long
 *   edge is at most this many pixels, in the same single encode (patient media
 *   uses 2560). Without it the image keeps its natural size, as directives need.
 * @returns {Promise<File>} a WebP File for images, the original File for PDFs
 * @throws if the type is unsupported or decoding/encoding fails
 */
export async function convertUploadFile(file, options = {}) {
  if (!file) throw new Error("No file provided");
  if (file.type === "application/pdf") return file; // PDFs are never converted
  if (isHeic(file)) return encodeHeicToWebp(file, options);
  if (SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    return encodeImageToWebp(file, options);
  }
  throw new Error(`Unsupported file type: ${file.type || "unknown"}`);
}

// Desktop Chrome/Firefox often report an empty MIME type for .heic/.heif files,
// so fall back to the extension when the type is blank.
export function isHeic(file) {
  const type = (file.type || "").toLowerCase();
  if (type === "image/heic" || type === "image/heif") return true;
  if (!type) return /\.(heic|heif)$/i.test(file.name || "");
  return false;
}

// Native-first: Safari/iOS can decode HEIC straight to canvas, so try that (no
// WASM fetched). Only on failure (desktop Chrome/Firefox) lazily load heic2any,
// which decodes to a lossless PNG; that PNG then takes the single shared WebP
// encode — so HEIC ends up with exactly one lossy step, like JPEG/PNG.
async function encodeHeicToWebp(file, options = {}) {
  try {
    return await encodeImageToWebp(file, options);
  } catch (nativeErr) {
    const pngBlob = await heicToPngBlob(file);
    const pngFile = new File([pngBlob], baseName(file.name) + ".png", {
      type: "image/png",
    });
    return encodeImageToWebp(pngFile, options);
  }
}

async function heicToPngBlob(file) {
  const mod = await import("heic2any");
  const heic2any = mod.default || mod;
  const result = await heic2any({ blob: file, toType: "image/png" });
  // Multi-image HEIC (Live Photos / bursts) yields an array; take the first.
  return Array.isArray(result) ? result[0] : result;
}

async function encodeImageToWebp(file, { maxEdge } = {}) {
  const source = await decodeImage(file);
  try {
    const canvas = document.createElement("canvas");
    const { width, height } = fitWithin(
      source.naturalWidth || source.width,
      source.naturalHeight || source.height,
      maxEdge
    );
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(source, 0, 0, width, height);
    const blob = await canvasToWebpBlob(canvas);
    return new File([blob], toWebpName(file.name), { type: "image/webp" });
  } finally {
    if (typeof source.close === "function") source.close(); // release ImageBitmap
  }
}

/** Scale (w, h) down so the long edge is at most maxEdge; never scales up. */
export function fitWithin(w, h, maxEdge) {
  const long = Math.max(w, h);
  if (!maxEdge || !long || long <= maxEdge) return { width: w, height: h };
  const k = maxEdge / long;
  return { width: Math.round(w * k), height: Math.round(h * k) };
}

// Decode via createImageBitmap (fast, off-thread, throws on undecodable bytes);
// fall back to an <img> load where createImageBitmap is unavailable.
async function decodeImage(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file);
  }
  return decodeViaImageElement(file);
}

function decodeViaImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image could not be decoded"));
    };
    img.src = url;
  });
}

function canvasToWebpBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("WebP encoding failed")),
      "image/webp",
      WEBP_QUALITY
    );
  });
}

function baseName(name) {
  return (name || "upload").replace(/\.[^./\\]+$/, "");
}

function toWebpName(name) {
  return `${baseName(name)}.webp`;
}
